import type { IceServer, SignalData } from '@repo/shared/protocol';

export type PeerState = 'connecting' | 'connected' | 'failed';

export type QualityLevel = 'good' | 'fair' | 'poor';

/** Wie gut die Leitung gerade ist. Wird aus den WebRTC-Statistiken gelesen. */
export interface PeerQuality {
  level: QualityLevel;
  /** Laufzeit hin und zurück in Millisekunden, null wenn der Browser sie nicht meldet */
  rtt: number | null;
  /** Anteil verlorener Pakete seit der letzten Messung, 0 bis 1 */
  loss: number;
}

export interface PeerLinkOptions {
  initiator: boolean;
  iceServers: IceServer[];
  iceTransportPolicy: RTCIceTransportPolicy;
  localStream: MediaStream;
  sendSignal: (data: SignalData) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onState: (state: PeerState) => void;
  onChatOpen: () => void;
  onChatMessage: (text: string) => void;
  onQuality: (quality: PeerQuality) => void;
}

/** So lange darf der Verbindungsaufbau dauern, danach geht es zum nächsten Match. */
const CONNECT_TIMEOUT_MS = 15_000;
/** "disconnected" erholt sich oft von selbst (z. B. WLAN → Mobilfunk) – so lange wird gewartet. */
const DISCONNECT_GRACE_MS = 5_000;
/** Obergrenze für die Video-Bitrate: schont den Upload und – falls über TURN – die Serverkosten. */
const MAX_VIDEO_BITRATE = 1_200_000;
export const MAX_CHAT_LENGTH = 500;
/** So oft wird die Leitung gemessen, solange ein Gespräch läuft. */
const QUALITY_EVERY_MS = 3_000;

/**
 * WebRTC-Verbindung zu genau einem Gegenüber.
 *
 * Die Rollen vergibt der Server (initiator erstellt das Offer), deshalb braucht es kein
 * "Perfect Negotiation". Mikro/Kamera aus läuft über track.enabled ohne Neuverhandlung.
 * Eingehende Signale werden strikt nacheinander verarbeitet, ICE-Kandidaten vor der
 * Remote-Description zwischengespeichert. Der Textchat läuft direkt über einen DataChannel.
 */
export class PeerLink {
  readonly #options: PeerLinkOptions;
  readonly #pc: RTCPeerConnection;
  readonly #connectTimer: number;
  #graceTimer: number | undefined;
  #channel: RTCDataChannel | null = null;
  #fallbackRemote: MediaStream | null = null;
  #pendingCandidates: RTCIceCandidateInit[] = [];
  #queue: Promise<void> = Promise.resolve();
  #state: PeerState = 'connecting';
  #closed = false;
  #qualityTimer: number | undefined;
  #lastLost = 0;
  #lastReceived = 0;

  constructor(options: PeerLinkOptions) {
    this.#options = options;
    const pc = new RTCPeerConnection({
      iceServers: options.iceServers,
      iceTransportPolicy: options.iceTransportPolicy,
      bundlePolicy: 'max-bundle',
    });
    this.#pc = pc;

    for (const track of options.localStream.getTracks()) pc.addTrack(track, options.localStream);

    pc.ontrack = (event) => {
      options.onRemoteStream(event.streams[0] ?? this.#addFallbackTrack(event.track));
    };
    pc.onicecandidate = ({ candidate }) => {
      if (!candidate) return;
      options.sendSignal({
        kind: 'candidate',
        candidate: {
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex,
          usernameFragment: candidate.usernameFragment,
        },
      });
    };
    pc.onconnectionstatechange = () => this.#onConnectionState();

    if (options.initiator) {
      this.#attachChannel(pc.createDataChannel('chat', { ordered: true }));
      this.#enqueue(async () => {
        await pc.setLocalDescription();
        this.#sendLocalDescription();
      });
    } else {
      pc.ondatachannel = (event) => this.#attachChannel(event.channel);
    }

    this.#connectTimer = window.setTimeout(() => {
      if (this.#state === 'connecting') this.#setState('failed');
    }, CONNECT_TIMEOUT_MS);
  }

  handleSignal(data: SignalData): void {
    this.#enqueue(async () => {
      const pc = this.#pc;
      if (data.kind === 'candidate') {
        if (!data.candidate) return;
        if (pc.remoteDescription) await this.#addCandidate(data.candidate);
        else this.#pendingCandidates.push(data.candidate);
        return;
      }

      const { description } = data;
      const expected = this.#options.initiator
        ? description.type === 'answer' && pc.signalingState === 'have-local-offer'
        : description.type === 'offer';
      if (!expected) return;

      await pc.setRemoteDescription(description);
      for (const candidate of this.#pendingCandidates.splice(0))
        await this.#addCandidate(candidate);
      if (description.type === 'offer') {
        await pc.setLocalDescription();
        this.#sendLocalDescription();
      }
    });
  }

  /** Kamera oder Mikrofon im laufenden Gespräch austauschen, ohne neu zu verhandeln. */
  async replaceTrack(track: MediaStreamTrack): Promise<void> {
    if (this.#closed) return;
    const sender = this.#pc.getSenders().find((entry) => entry.track?.kind === track.kind);
    if (!sender) return;
    try {
      await sender.replaceTrack(track);
    } catch (error) {
      console.warn('Spur nicht ausgetauscht', error);
      return;
    }
    if (track.kind === 'video') await this.#limitVideoBitrate();
  }

  sendChat(text: string): boolean {
    if (this.#channel?.readyState !== 'open') return false;
    this.#channel.send(JSON.stringify({ type: 'chat', text: text.slice(0, MAX_CHAT_LENGTH) }));
    return true;
  }

  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    window.clearTimeout(this.#connectTimer);
    window.clearTimeout(this.#graceTimer);
    window.clearInterval(this.#qualityTimer);
    this.#channel?.close();
    const pc = this.#pc;
    pc.ontrack = pc.onicecandidate = pc.onconnectionstatechange = pc.ondatachannel = null;
    pc.close();
  }

  #enqueue(task: () => Promise<void>): void {
    this.#queue = this.#queue.then(async () => {
      if (this.#closed) return;
      try {
        await task();
      } catch (error) {
        console.warn('WebRTC-Verhandlung fehlgeschlagen', error);
        this.#setState('failed');
      }
    });
  }

  async #addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await this.#pc.addIceCandidate(candidate);
    } catch (error) {
      console.warn('ICE-Kandidat abgelehnt', error);
    }
  }

  #sendLocalDescription(): void {
    const description = this.#pc.localDescription;
    if (this.#closed || !description) return;
    if (description.type !== 'offer' && description.type !== 'answer') return;
    this.#options.sendSignal({
      kind: 'description',
      description: { type: description.type, sdp: description.sdp },
    });
  }

  #addFallbackTrack(track: MediaStreamTrack): MediaStream {
    this.#fallbackRemote ??= new MediaStream();
    this.#fallbackRemote.addTrack(track);
    return this.#fallbackRemote;
  }

  #attachChannel(channel: RTCDataChannel): void {
    this.#channel = channel;
    channel.onopen = () => {
      if (!this.#closed) this.#options.onChatOpen();
    };
    channel.onmessage = (event: MessageEvent) => {
      if (this.#closed || typeof event.data !== 'string') return;
      if (event.data.length > 4 * MAX_CHAT_LENGTH) return;
      try {
        const msg: unknown = JSON.parse(event.data);
        if (isChatMessage(msg)) this.#options.onChatMessage(msg.text.slice(0, MAX_CHAT_LENGTH));
      } catch {
        // Unlesbares vom Gegenüber ignorieren
      }
    };
  }

  #onConnectionState(): void {
    switch (this.#pc.connectionState) {
      case 'connected':
        window.clearTimeout(this.#connectTimer);
        window.clearTimeout(this.#graceTimer);
        this.#setState('connected');
        void this.#limitVideoBitrate();
        this.#watchQuality();
        break;
      case 'disconnected':
        window.clearTimeout(this.#graceTimer);
        this.#graceTimer = window.setTimeout(() => {
          if (this.#pc.connectionState !== 'connected') this.#setState('failed');
        }, DISCONNECT_GRACE_MS);
        break;
      case 'failed':
        this.#setState('failed');
        break;
      default:
        break;
    }
  }

  async #limitVideoBitrate(): Promise<void> {
    for (const sender of this.#pc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      const parameters = sender.getParameters();
      const [encoding] = parameters.encodings;
      if (!encoding) continue;
      encoding.maxBitrate = MAX_VIDEO_BITRATE;
      try {
        await sender.setParameters(parameters);
      } catch {
        // Nicht jeder Browser erlaubt das – dann eben ohne Obergrenze.
      }
    }
  }

  #watchQuality(): void {
    if (this.#qualityTimer !== undefined) return;
    void this.#measureQuality();
    this.#qualityTimer = window.setInterval(() => void this.#measureQuality(), QUALITY_EVERY_MS);
  }

  /**
   * Paketverlust, Laufzeit und Zittern des eingehenden Videos. Gemessen wird nur die Differenz
   * zur letzten Messung, sonst schleppt ein kurzer Aussetzer die Anzeige ewig mit.
   */
  async #measureQuality(): Promise<void> {
    if (this.#closed) return;
    let report: RTCStatsReport;
    try {
      report = await this.#pc.getStats();
    } catch {
      return;
    }
    if (this.#closed) return;

    const entries: StatsEntry[] = [];
    report.forEach((entry) => entries.push(entry as StatsEntry));

    let rtt: number | null = null;
    let lost = 0;
    let received = 0;
    let jitter = 0;
    for (const entry of entries) {
      if (
        entry.type === 'candidate-pair' &&
        entry.state === 'succeeded' &&
        typeof entry.currentRoundTripTime === 'number'
      ) {
        rtt = entry.currentRoundTripTime * 1_000;
      } else if (
        entry.type === 'remote-inbound-rtp' &&
        rtt === null &&
        typeof entry.roundTripTime === 'number'
      ) {
        rtt = entry.roundTripTime * 1_000;
      } else if (entry.type === 'inbound-rtp' && entry.kind === 'video') {
        if (typeof entry.packetsLost === 'number') lost = entry.packetsLost;
        if (typeof entry.packetsReceived === 'number') received = entry.packetsReceived;
        if (typeof entry.jitter === 'number') jitter = entry.jitter * 1_000;
      }
    }

    const newLost = Math.max(0, lost - this.#lastLost);
    const newReceived = Math.max(0, received - this.#lastReceived);
    this.#lastLost = lost;
    this.#lastReceived = received;
    const seen = newLost + newReceived;
    const loss = seen > 0 ? newLost / seen : 0;

    const poor = loss > 0.08 || jitter > 60 || (rtt !== null && rtt > 500);
    const fair = loss > 0.02 || jitter > 30 || (rtt !== null && rtt > 250);
    this.#options.onQuality({
      level: poor ? 'poor' : fair ? 'fair' : 'good',
      rtt: rtt === null ? null : Math.round(rtt),
      loss,
    });
  }

  #setState(state: PeerState): void {
    if (this.#closed || this.#state === state) return;
    this.#state = state;
    this.#options.onState(state);
  }
}

/** Nur die Felder der WebRTC-Statistik, die hier gebraucht werden. */
interface StatsEntry {
  type?: string;
  kind?: string;
  state?: string;
  currentRoundTripTime?: number;
  roundTripTime?: number;
  packetsLost?: number;
  packetsReceived?: number;
  jitter?: number;
}

function isChatMessage(value: unknown): value is { type: 'chat'; text: string } {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { type?: unknown; text?: unknown };
  return candidate.type === 'chat' && typeof candidate.text === 'string';
}
